import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2Icon } from "lucide-react";

/**
 * Admin-side save confirmation. Mirrors the school portal's SaveToast feel (a brief,
 * fixed bottom-right "Opgeslagen" that auto-dismisses), but is generic: it is fired
 * imperatively after a successful save rather than being driven by an auto-save state
 * machine, and it lets each caller pass its own short message.
 *
 * Errors keep using each page's existing inline error handling, so this toast only ever
 * shows a green success confirmation.
 *
 * Usage:
 *   const saved = useAdminSaveToast();
 *   // ... after a successful save:
 *   saved.show();                    // default "Opgeslagen"
 *   saved.show("Activiteit opgeslagen");
 *   // render once, anywhere in the component:
 *   {saved.toast}
 */
export function useAdminSaveToast(defaultMessage = "Opgeslagen") {
  const [message, setMessage] = useState(defaultMessage);
  const [visible, setVisible] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const show = useCallback(
    (text?: string) => {
      setMessage(text ?? defaultMessage);
      setVisible(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setVisible(false), 2200);
    },
    [defaultMessage]
  );

  // Clear the pending hide-timer if the component unmounts mid-toast.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const toast = <AdminSaveToast visible={visible} message={message} />;

  return { show, toast };
}

/**
 * The presentational toast. Normally rendered for you via useAdminSaveToast; exported in
 * case a caller wants to drive visibility itself.
 */
export function AdminSaveToast({ visible, message }: { visible: boolean; message: string }) {
  return (
    <div
      aria-live="polite"
      className={`pointer-events-none fixed bottom-5 right-5 z-50 transition-all duration-200 ${
        visible ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
      }`}
    >
      <div className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3.5 py-2 text-sm text-emerald-700 shadow-lg">
        <CheckCircle2Icon size={15} />
        <span className="font-medium">{message}</span>
      </div>
    </div>
  );
}
